/**
 * Orders Entered stacked bar chart — memoized so list/table churn in
 * OrdersSection does not redraw the bars (rerender-memo).
 */

import { memo } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';

export type OrdersEnteredChartDay = {
  day: string;
  label: string;
  count: number;
  completed: number;
  open: number;
  periodHighlighted: boolean;
};

type OrdersEnteredChartProps = {
  days: OrdersEnteredChartDay[];
  maxCount: number;
  todayKey: string;
  isSingleDayPeriod: boolean;
  selectedPeriod: string;
  trendChange: number;
};

function OrdersEnteredChart({
  days,
  maxCount,
  todayKey,
  isSingleDayPeriod,
  selectedPeriod,
  trendChange,
}: OrdersEnteredChartProps) {
  if (days.length === 0) return null;

  return (
    <div className="bg-card/50 rounded-lg p-5 border border-border min-w-0">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div>
          <h4 className="text-lg font-semibold text-white">Orders Entered</h4>
          <p className="text-sm text-muted-foreground mt-1">
            {isSingleDayPeriod ? (
              <span className="text-muted-foreground">
                By date entered · last 7 days
                {' — '}
                {selectedPeriod === 'today'
                  ? 'Today'
                  : selectedPeriod === 'yesterday'
                    ? 'Yesterday'
                    : 'selected day'}
                {' '}
                highlighted · Today live
              </span>
            ) : trendChange !== 0 ? (
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-medium ${
                  trendChange > 0
                    ? 'bg-success/20 text-success border border-success/30'
                    : 'bg-destructive/20 text-destructive border border-destructive/30'
                }`}
              >
                {trendChange > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {trendChange > 0 ? '+' : ''}
                {trendChange.toFixed(1)}% vs previous period
              </span>
            ) : (
              <span className="text-muted-foreground">By date entered · completed vs still open</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-success/80" />
            Completed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-chart-5/70" />
            Open
          </span>
        </div>
      </div>
      <div className="flex items-end gap-2 sm:gap-3.5">
        {days.map((d) => {
          const isToday = d.day === todayKey;
          const isFocus = d.periodHighlighted;
          const isEmpty = d.count === 0;
          const cancelled = Math.max(0, d.count - d.completed - d.open);
          const completionPct = d.count > 0
            ? Math.round((d.completed / d.count) * 100)
            : null;
          const stackPx = isEmpty
            ? 160
            : Math.max(28, Math.round((d.count / maxCount) * 160));
          const completedPx = !isEmpty && d.completed > 0
            ? Math.max(4, Math.round((d.completed / d.count) * stackPx))
            : 0;
          const openPx = !isEmpty && d.open > 0
            ? Math.max(4, Math.round((d.open / d.count) * stackPx))
            : 0;
          const cancelledPx = !isEmpty && cancelled > 0
            ? Math.max(2, Math.round((cancelled / d.count) * stackPx))
            : 0;
          const dimOthers = isSingleDayPeriod && !isFocus && !isToday;

          return (
            <div
              key={d.day}
              className={`flex-1 flex flex-col items-center min-w-0 ${dimOthers ? 'opacity-55' : ''}`}
              title={`${d.day}: ${d.count} entered (${d.completed} completed, ${d.open} open${cancelled > 0 ? `, ${cancelled} cancelled` : ''})`}
            >
              <div className="mb-2.5 text-center min-h-10 flex flex-col justify-end gap-0.5">
                {isToday ? (
                  <span className="text-xs font-bold uppercase tracking-wide text-ring">
                    ● Live
                  </span>
                ) : null}
                {isEmpty ? (
                  <span className="text-xs font-medium text-muted-foreground">No orders</span>
                ) : (
                  <span className="tabular-nums text-sm font-bold leading-none">
                    <span className={isToday ? 'text-ring' : 'text-success'}>
                      {d.completed.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground mx-1 text-xs font-medium">·</span>
                    <span className="text-chart-5">{d.open.toLocaleString()}</span>
                  </span>
                )}
              </div>

              {isEmpty ? (
                <div
                  className="w-full rounded-md border border-dashed border-border/80 bg-transparent"
                  style={{ height: stackPx }}
                />
              ) : (
                <div
                  className={`w-full flex flex-col justify-end overflow-hidden rounded-t-md rounded-b-sm ${
                    isToday ? 'ring-2 ring-ring/80' : isFocus ? 'ring-1 ring-ring/40' : ''
                  }`}
                  style={{ height: stackPx }}
                >
                  {openPx > 0 ? (
                    <div
                      className="w-full shrink-0 bg-gradient-to-b from-chart-5 to-chart-5"
                      style={{ height: openPx }}
                    />
                  ) : null}
                  {cancelledPx > 0 ? (
                    <div
                      className="w-full shrink-0 bg-accent/50"
                      style={{ height: cancelledPx }}
                      title={`${cancelled} cancelled`}
                    />
                  ) : null}
                  {completedPx > 0 ? (
                    <div
                      className={`w-full shrink-0 min-h-1 ${
                        isToday
                          ? 'bg-gradient-to-b from-ring/40 to-ring/15'
                          : 'bg-gradient-to-b from-success to-success'
                      }`}
                      style={{ height: completedPx }}
                    />
                  ) : null}
                </div>
              )}

              <div className="mt-1 min-h-4 font-mono text-xs tabular-nums text-muted-foreground">
                {completionPct != null ? `${completionPct}%` : '\u00a0'}
              </div>
              <div
                className={`mt-1 text-xs font-bold tracking-wide ${
                  isToday ? 'text-ring' : isFocus ? 'text-ring' : 'text-muted-foreground'
                }`}
              >
                {d.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default memo(OrdersEnteredChart);
